import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppStateService } from '../app-state/app-state.service';
import { ConnectionService } from '../connection/connection.service';
import { CacheService } from '../cache/cache.service';
import { FileDataService } from '../file-data/file-data.service';
import { GeneralHelpers } from '../../helpers/general.helper';
import { ensureParentDirs } from '../../helpers/emscripten-fs.helper';
import { MessageService } from '../message/message.service';
import { API_BASE, RELOAD_NOTEBOOK_FILES } from '../../constants/general.constants';
import { Connection, ConnectionType } from '@app/shared/interfaces';
import { ExecutionContext } from '@app/shared/interfaces/chunk/chunk-context.interface';
import { isDuplicateEntry } from '@app/shared/interfaces/server-error.interface';
import type { StepParams } from '@enlighten/ts-shared/step-api';
import { firstValueFrom } from 'rxjs';

export interface BoxFileMetadata {
  id: string;
  name: string;
  size: number;
  /** `null` when Box sends none, as in enoch. */
  sha1: string | null;
  /** `null` when Box sends none, as in enoch. */
  created_at: string | null;
  modified_at: string;
  parent: { id: string; name: string };
}

export interface BoxFolderItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
}

export interface BoxComment {
  id: string;
  message: string;
  created_at?: string;
  created_by?: { id: string; name: string; login?: string };
}

// Box connection is just a Connection with ConnectionType.Box
type BoxConnection = Connection;

/**
 * BoxService - uses nc-server proxy endpoints for all Box operations
 *
 * This service makes HTTP calls to nc-server which handles:
 * - CCG/JWT token acquisition and caching
 * - Box API authentication
 * - Error handling and status code mapping
 */
@Injectable({
  providedIn: 'root'
})
export class BoxService {
  private http = inject(HttpClient);
  private appState = inject(AppStateService);
  private connectionService = inject(ConnectionService);
  private cacheService = inject(CacheService);
  private fileDataService = inject(FileDataService);
  private messageService = inject(MessageService);

  // Emscripten/Pyodide in-memory filesystem handle. Its surface is only loosely
  // described by Pyodide's own types, so it is kept `any` (partly inherent per the
  // 2026-07-11 audit; the Pyodide boundary is retyped with the Worker-isolation
  // work, ADR-0005 — not this pass).
  private pyodideFS: any = null;

  /**
   * Set Pyodide FS reference so downloaded files are written to the
   * in-memory filesystem for immediate access within the same step.
   */
  setPyodideFS(fs: any): void {
    this.pyodideFS = fs;
  }

  /**
   * Resolve connection name to connection object
   * Searches for connection by name in current project
   */
  private async getConnectionByName(connectionName: string): Promise<BoxConnection> {
    const projectId = this.appState.projectId();
    if (!projectId) {
      throw new Error('No active project');
    }

    const connections = await firstValueFrom(this.connectionService.getConnections(projectId));
    const connection = connections.find(c => c.name === connectionName && c.type === ConnectionType.Box);

    if (!connection) {
      throw new Error(`Box connection '${connectionName}' not found in project`);
    }

    return connection;
  }

  /**
   * Extract a human-readable message from a failed Box request.
   *
   * The server reshapes upstream Box errors into the unified envelope
   * (`{ code, message, details }`, SCRUM-801). For JSON requests the parsed body
   * is on `HttpErrorResponse.error`; for the download endpoint (which uses
   * `responseType: 'blob'`) the body arrives as a `Blob`, so read and parse it
   * first. Falls back to the legacy `error` field and then Angular's synthetic
   * `error.message`.
   */
  private async boxErrorMessage(error: unknown): Promise<string> {
    let body: unknown = (error as { error?: unknown })?.error;
    if (body instanceof Blob) {
      try {
        body = JSON.parse(await body.text());
      } catch {
        body = undefined;
      }
    }
    const bodyObj = body as { message?: string; error?: string } | null | undefined;
    const errObj = error as { message?: string } | null | undefined;
    return bodyObj?.message ?? bodyObj?.error ?? errObj?.message ?? String(error);
  }

  /**
   * Call Box proxy endpoint.
   *
   * Returns the upstream Box API JSON verbatim; its shape varies per endpoint and
   * is not modelled here, so the return stays `any` and callers read the fields
   * they expect.
   */
  private async callProxy(
    projectId: number,
    connectionId: number,
    method: string,
    path: string,
    body?: unknown
  ): Promise<any> {
    const response = await firstValueFrom(
      this.http.post(`${API_BASE}/project/${projectId}/box/proxy`, {
        connection_id: connectionId,
        method,
        path,
        body
      })
    );

    return response || {};
  }

  // The public methods below are invoked from step code through the JS/Python
  // bridges, which parse and validate the step's arguments with the shared
  // `BOX` parsers (EHQ-10). Ids arrive as strings and an optional name that was
  // empty arrives as `undefined`.

  /**
   * Download file from Box to workflow directory
   * Saves the file to the workflow's cache (notebook files)
   */
  public async downloadFile(params: StepParams['BOX.downloadFile'], chunkContext?: ExecutionContext): Promise<void> {
    const { connectionName, fileId, workflowFileName } = params;
    await this.downloadBoxFile(connectionName, fileId, workflowFileName, undefined, chunkContext);
  }

  public async downloadFileVersion(params: StepParams['BOX.downloadFileVersion'], chunkContext?: ExecutionContext): Promise<void> {
    const { connectionName, fileId, versionId, workflowFileName } = params;
    await this.downloadBoxFile(connectionName, fileId, workflowFileName, versionId, chunkContext);
  }

  private async downloadBoxFile(
    connectionName: string,
    fileId: string,
    workflowFileName: string,
    versionId?: string,
    chunkContext?: ExecutionContext,
  ): Promise<void> {
    const connection = await this.getConnectionByName(connectionName);
    const payload: { connection_id: number; file_id: string; version_id?: string } = {
      connection_id: connection.connectionId!,
      file_id: fileId,
    };
    if (versionId !== undefined) {
      payload.version_id = versionId;
    }

    const label = versionId ? `${fileId} v${versionId}` : fileId;

    try {
      // Use blob response type for lower browser heap pressure than arraybuffer
      const blob = await firstValueFrom(
        this.http.post(
          `${API_BASE}/project/${connection.projectId}/box/download`,
          payload,
          { responseType: 'blob' },
        )
      );

      if (!blob) {
        throw new Error('Failed to download file from Box');
      }

      // Stored under the full relative path, like a step's saveFile (EHQ-455).
      const cacheName = GeneralHelpers.cacheFileName(workflowFileName);
      const fileName =
        GeneralHelpers.directoryFromString(workflowFileName) +
        GeneralHelpers.fileNameFromString(workflowFileName);
      const fileExtension = GeneralHelpers.fileExtensionFromString(workflowFileName);
      // Blob is accepted directly by File constructor via prepareFileData
      const formData = this.fileDataService.prepareFileData(fileName, fileExtension, blob);
      formData.append('name', cacheName);

      const updated = await this.writeWorkflowCacheFile(cacheName, formData);
      if (chunkContext) {
        const verb = updated ? 'Updated' : 'Downloaded';
        chunkContext.addMessage(`${verb} Box file ${label} as ${workflowFileName}`, 'success');
      }

      if (this.pyodideFS) {
        try {
          // Convert blob to Uint8Array only for Pyodide FS write
          const data = new Uint8Array(await blob.arrayBuffer());
          ensureParentDirs(this.pyodideFS, workflowFileName);
          this.pyodideFS.writeFile(workflowFileName, data);
          console.log(`[BoxService] Downloaded ${(data.length / 1024 / 1024).toFixed(1)} MB to Pyodide FS: ${workflowFileName}`);
        } catch (fsError) {
          console.warn('Failed to write downloaded file to Pyodide FS:', fsError);
        }
      }

      this.messageService.sendMessage(RELOAD_NOTEBOOK_FILES);
    } catch (error) {
      const errorMsg = `Failed to download Box file: ${await this.boxErrorMessage(error)}`;
      if (chunkContext) {
        chunkContext.addMessage(errorMsg, 'danger');
      }
      throw new Error(errorMsg);
    }
  }

  /**
   * Idempotently write a file to the workflow cache.
   *
   * The cache enforces a (cache_id, name) unique constraint server-side
   * (workflow_file_name_unique). The previous implementation did a list-by-name
   * "exists?" check and either updated or created accordingly, which fails in
   * two cases:
   *   1. The locally GET'd cache list lags an earlier write (HTTP cache /
   *      observable replay / inflight request) and reports "not found" when
   *      a row was just created.
   *   2. A concurrent client (or this same client across runs) creates the
   *      row between our list and our create.
   * Either way the server returns 400 with
   *   {"message": "duplicate entry: duplicate key value violates unique
   *    constraint \"workflow_file_name_unique\""}
   * and the workflow step fails for a reason the user can't act on.
   *
   * Fix: keep the fast path, but on unique-violation, re-list (fresh) and
   * upgrade to an update by fileId. Returns true if the row was updated
   * (existed already) and false if it was newly created.
   */
  private async writeWorkflowCacheFile(
    workflowFileName: string,
    formData: FormData,
  ): Promise<boolean> {
    const findByName = (content: unknown): { fileId: number } | undefined => {
      if (!Array.isArray(content)) return undefined;
      return content.find(
        (f: unknown) => !!f && typeof f === 'object' && (f as { name?: unknown }).name === workflowFileName,
      ) as { fileId: number } | undefined;
    };

    const existingFile = findByName(
      await firstValueFrom(this.cacheService.getCacheContent()),
    );

    if (existingFile) {
      await firstValueFrom(
        this.cacheService.updateCacheContentFile(existingFile.fileId, formData),
      );
      return true;
    }

    try {
      // A unique violation falls through to update-by-id below, so no toast.
      await firstValueFrom(this.cacheService.createCacheContent(formData, { duplicateHandled: true }));
      return false;
    } catch (err) {
      if (!BoxService.isCacheUniqueViolation(err)) {
        throw err;
      }
      // Server says the row exists; refresh the list and update by id.
      const found = findByName(
        await firstValueFrom(this.cacheService.getCacheContent()),
      );
      if (!found) {
        // Constraint fires but row still not visible to us — surface original.
        throw err;
      }
      await firstValueFrom(
        this.cacheService.updateCacheContentFile(found.fileId, formData),
      );
      return true;
    }
  }

  private static isCacheUniqueViolation(err: unknown): boolean {
    const e = err as {
      status?: number;
      error?: { code?: unknown; details?: { constraint?: unknown } };
    } | null | undefined;
    // A duplicate is a 409 DUPLICATE_ENTRY with the constraint in details (EHQ-371).
    return isDuplicateEntry(e) && e?.error?.details?.constraint === 'workflow_file_name_unique';
  }

  /**
   * Read a workflow file as a number array, trying cache first then Pyodide FS.
   */
  private async readWorkflowFileContent(workflowFileName: string): Promise<Uint8Array> {
    const cacheContent = await firstValueFrom(this.cacheService.getCacheContent());
    const file = GeneralHelpers.findElementInArrayByProp(
      cacheContent,
      'name',
      workflowFileName
    );

    if (file) {
      const fileData = await firstValueFrom(
        this.cacheService.getCacheContentFile(file['fileId'], 'arraybuffer')
      );
      if (!fileData) {
        throw new Error(`Failed to read file ${workflowFileName} from cache`);
      }
      return new Uint8Array(fileData);
    } else if (this.pyodideFS) {
      try {
        return this.pyodideFS.readFile(`/home/pyodide/${workflowFileName}`);
      } catch {
        return this.pyodideFS.readFile(workflowFileName);
      }
    } else {
      throw new Error(`File ${workflowFileName} not found in workflow files or Pyodide FS`);
    }
  }

  /**
   * Upload file from workflow directory to Box
   */
  public async uploadFile(params: StepParams['BOX.uploadFile'], chunkContext?: ExecutionContext): Promise<string> {
    const { connectionName, workflowFileName, folderId, boxFileName } = params;
    const connection = await this.getConnectionByName(connectionName);
    const fileName = boxFileName || workflowFileName;

    try {
      const fileData = await this.readWorkflowFileContent(workflowFileName);
      const formData = new FormData();
      formData.append('connection_id', String(connection.connectionId!));
      formData.append('folder_id', folderId);
      formData.append('file_name', fileName);
      formData.append('file', new Blob([fileData.buffer as ArrayBuffer]), fileName);

      const response = await firstValueFrom(
        this.http.post<{ entries?: { id: string }[] }>(`${API_BASE}/project/${connection.projectId}/box/upload-multipart`, formData)
      );

      if (!response?.entries?.[0]) {
        throw new Error('Invalid response from Box upload');
      }

      const uploadedFileId = response.entries[0].id;

      if (chunkContext) {
        chunkContext.addMessage(`Uploaded ${workflowFileName} to Box as file ${uploadedFileId}`, 'success');
      }

      return uploadedFileId;
    } catch (error) {
      const errorMsg = `Failed to upload file to Box: ${await this.boxErrorMessage(error)}`;
      if (chunkContext) {
        chunkContext.addMessage(errorMsg, 'danger');
      }
      throw new Error(errorMsg);
    }
  }

  /**
   * Upload a new version of an existing file in Box (preserves file ID)
   */
  public async uploadNewVersion(params: StepParams['BOX.uploadNewVersion'], chunkContext?: ExecutionContext): Promise<string> {
    const { connectionName, fileId, workflowFileName } = params;
    const connection = await this.getConnectionByName(connectionName);

    try {
      const fileData = await this.readWorkflowFileContent(workflowFileName);
      const formData = new FormData();
      formData.append('connection_id', String(connection.connectionId!));
      formData.append('file_id', fileId);
      formData.append('file', new Blob([fileData.buffer as ArrayBuffer]), workflowFileName);

      const response = await firstValueFrom(
        this.http.post<{ entries?: { id: string }[] }>(`${API_BASE}/project/${connection.projectId}/box/upload-version-multipart`, formData)
      );

      if (!response?.entries?.[0]) {
        throw new Error('Invalid response from Box upload version');
      }

      const uploadedFileId = response.entries[0].id;

      if (chunkContext) {
        chunkContext.addMessage(`Uploaded new version of ${workflowFileName} to Box file ${uploadedFileId}`, 'success');
      }

      return uploadedFileId;
    } catch (error) {
      const errorMsg = `Failed to upload new version to Box: ${await this.boxErrorMessage(error)}`;
      if (chunkContext) {
        chunkContext.addMessage(errorMsg, 'danger');
      }
      throw new Error(errorMsg);
    }
  }

  /**
   * Get file metadata
   */
  public async getFileMetadata(params: StepParams['BOX.getFileMetadata'], _chunkContext?: ExecutionContext): Promise<BoxFileMetadata> {
    const { connectionName, fileId } = params;
    const connection = await this.getConnectionByName(connectionName);

    const file = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'GET',
      `/files/${fileId}`
    );

    return {
      id: file.id,
      name: file.name,
      size: file.size,
      sha1: file.sha1 || null,
      created_at: file.created_at || null,
      modified_at: file.modified_at,
      parent: {
        id: file.parent.id,
        name: file.parent.name
      }
    };
  }

  /**
   * List folder contents
   */
  public async listFolder(params: StepParams['BOX.listFolder'], _chunkContext?: ExecutionContext): Promise<{total_count: number, entries: BoxFolderItem[]}> {
    const { connectionName, folderId } = params;
    const connection = await this.getConnectionByName(connectionName);

    const result = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'GET',
      `/folders/${folderId}/items`
    );

    return {
      total_count: result.total_count,
      entries: result.entries.map((item: { id: string; name: string; type: 'file' | 'folder' }) => ({
        id: item.id,
        name: item.name,
        type: item.type
      }))
    };
  }

  /**
   * Create folder
   */
  public async createFolder(params: StepParams['BOX.createFolder'], _chunkContext?: ExecutionContext): Promise<{id: string, name: string}> {
    const { connectionName, name, parentId } = params;
    const connection = await this.getConnectionByName(connectionName);

    const folder = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'POST',
      '/folders',
      {
        name,
        parent: { id: parentId }
      }
    );

    return {
      id: folder.id,
      name: folder.name
    };
  }

  /**
   * Delete file
   */
  public async deleteFile(params: StepParams['BOX.deleteFile'], _chunkContext?: ExecutionContext): Promise<void> {
    const { connectionName, fileId } = params;
    const connection = await this.getConnectionByName(connectionName);

    await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'DELETE',
      `/files/${fileId}`
    );
  }

  /**
   * Delete folder
   */
  public async deleteFolder(params: StepParams['BOX.deleteFolder'], _chunkContext?: ExecutionContext): Promise<void> {
    const { connectionName, folderId } = params;
    const connection = await this.getConnectionByName(connectionName);

    // recursive=true deletes folder even if it contains items
    await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'DELETE',
      `/folders/${folderId}?recursive=true`
    );
  }

  /**
   * Search files
   */
  public async searchFiles(params: StepParams['BOX.searchFiles'], _chunkContext?: ExecutionContext): Promise<{total_count: number, entries: BoxFolderItem[]}> {
    const { connectionName, query } = params;
    const connection = await this.getConnectionByName(connectionName);

    const result = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'GET',
      `/search?query=${encodeURIComponent(query)}`
    );

    return {
      total_count: result.total_count,
      entries: result.entries.map((item: { id: string; name: string; type: 'file' | 'folder' }) => ({
        id: item.id,
        name: item.name,
        type: item.type
      }))
    };
  }

  /**
   * Get shared link for file
   */
  public async getSharedLink(params: StepParams['BOX.getSharedLink'], _chunkContext?: ExecutionContext): Promise<string> {
    const { connectionName, fileId } = params;
    const connection = await this.getConnectionByName(connectionName);

    const updatedFile = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'PUT',
      `/files/${fileId}`,
      {
        shared_link: {
          access: 'open'
        }
      }
    );

    return updatedFile.shared_link.url;
  }

  /**
   * Move file to different folder
   */
  public async moveFile(params: StepParams['BOX.moveFile'], _chunkContext?: ExecutionContext): Promise<void> {
    const { connectionName, fileId, newParentId } = params;
    const connection = await this.getConnectionByName(connectionName);

    await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'PUT',
      `/files/${fileId}`,
      {
        parent: { id: newParentId }
      }
    );
  }

  /**
   * Copy file to different folder
   */
  public async copyFile(params: StepParams['BOX.copyFile'], _chunkContext?: ExecutionContext): Promise<string> {
    const { connectionName, fileId, newParentId, newName } = params;
    const connection = await this.getConnectionByName(connectionName);

    const body: { parent: { id: string }; name?: string } = {
      parent: { id: newParentId }
    };
    if (newName) {
      body.name = newName;
    }

    const copiedFile = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'POST',
      `/files/${fileId}/copy`,
      body
    );

    return copiedFile.id;
  }

  /**
   * List version history for a Box file (nc-server proxy).
   */
  public async getFileVersions(params: StepParams['BOX.getFileVersions'], _chunkContext?: ExecutionContext): Promise<unknown> {
    const { connectionName, fileId } = params;
    const connection = await this.getConnectionByName(connectionName);

    return await firstValueFrom(
      this.http.post(`${API_BASE}/project/${connection.projectId}/box/file-versions`, {
        connection_id: connection.connectionId!,
        file_id: fileId,
      })
    );
  }

  /**
   * Get the comments on a Box file (GET /files/{id}/comments).
   * A read: gates as List server-side and downscopes to item_preview.
   */
  public async getFileComments(params: StepParams['BOX.getFileComments'], _chunkContext?: ExecutionContext): Promise<{total_count: number, entries: BoxComment[]}> {
    const { connectionName, fileId } = params;
    const connection = await this.getConnectionByName(connectionName);

    const result = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'GET',
      `/files/${encodeURIComponent(fileId)}/comments`
    );

    return {
      total_count: result.total_count,
      entries: (result.entries ?? []).map((c: {
        id: string;
        message?: string;
        tagged_message?: string;
        created_at?: string;
        created_by?: { id: string; name: string; login?: string };
      }) => ({
        id: c.id,
        message: c.message ?? c.tagged_message ?? '',
        created_at: c.created_at ?? undefined,
        created_by: c.created_by
          ? { id: c.created_by.id, name: c.created_by.name, login: c.created_by.login }
          : undefined,
      })),
    };
  }

  /**
   * Add a comment to a Box file (POST /comments).
   * A write: gates as the dedicated Comment operation server-side and runs on
   * the parent token (Box does not grant this endpoint to a downscoped token).
   */
  public async addComment(params: StepParams['BOX.addComment'], _chunkContext?: ExecutionContext): Promise<BoxComment> {
    // The parser has already rejected an empty message and trimmed it.
    const { connectionName, fileId, message } = params;
    const connection = await this.getConnectionByName(connectionName);

    const comment = await this.callProxy(
      connection.projectId,
      connection.connectionId!,
      'POST',
      '/comments',
      {
        item: { type: 'file', id: fileId },
        message,
      }
    );

    return {
      id: comment.id,
      message: comment.message ?? comment.tagged_message ?? '',
      created_at: comment.created_at ?? undefined,
      created_by: comment.created_by
        ? { id: comment.created_by.id, name: comment.created_by.name, login: comment.created_by.login }
        : undefined,
    };
  }
}
